/**
 * ScreenModeButton: "보기" 드롭다운 버튼.
 *
 * v1 원본: RenewalComponents/SVContent/Header/ScreenButton/ScreenButtonPresenter.js
 * v2 변환: 단일 함수형 컴포넌트
 *
 * - 그리드뷰 / 사이드뷰 / 발표자뷰 선택
 * - 자동정렬 / 발언자 우선 / 참여자명 정렬
 */
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { uiActions } from '@/store/slices/uiSlice';

const SCREEN_MODE_LIST = [
  { code: 'GRID' as const, icon: 'iconGridview', text: '그리드뷰' },
  { code: 'SIDE' as const, icon: 'iconSideview', text: '사이드뷰' },
  { code: 'PRESENTER' as const, icon: 'iconPubview', text: '발표자뷰' },
];

const SORT_LIST = [
  { code: 'AUTO' as const, icon: 'iconSortAuto', text: '자동정렬' },
  { code: 'SPEAKER' as const, icon: 'iconSortSpeaker', text: '발언자 우선 정렬' },
  { code: 'NAME' as const, icon: 'iconSortMember', text: '참여자명 정렬' },
];

const ScreenModeButton: React.FC = () => {
  const dispatch = useAppDispatch();
  const screenMode = useAppSelector((s) => s.ui.screenMode);
  const screenSort = useAppSelector((s) => s.ui.screenSort);
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const selectedScreenMode = SCREEN_MODE_LIST.find(({ code }) => screenMode === code) || SCREEN_MODE_LIST[0];

  // 외부 클릭 닫기
  useEffect(() => {
    if (!isOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  const handleSetMode = useCallback((code: string) => {
    dispatch(uiActions.setScreenMode(code as 'GRID' | 'SIDE' | 'PRESENTER'));
    setIsOpen(false);
  }, [dispatch]);

  const handleSetSort = useCallback((code: string) => {
    dispatch(uiActions.setScreenSort(code as 'AUTO' | 'SPEAKER' | 'NAME'));
    setIsOpen(false);
  }, [dispatch]);

  return (
    <div className="viewerHeader__btnItem" ref={containerRef}>
      <button
        type="button"
        className="viewerHeader__btn"
        onClick={() => setIsOpen((v) => !v)}
      >
        <span className={`viewerHeader__btnIcon ${selectedScreenMode.icon}`}></span>
        {'보기'}
      </button>
      <div className={`viewerHeader__layer${isOpen ? ' open' : ''}`} style={{zIndex:1203}}>
        <ul className="viewerHeader__layerList" style={{overflowX:"hidden"}}>
          {SCREEN_MODE_LIST.map(({ code, icon, text }) => (
            <li key={code}>
              <button
                type="button"
                className={`viewerHeader__layerListBtn${code === screenMode ? ' selected' : ''}`}
                onClick={() => handleSetMode(code)}
              >
                <span className={`viewerHeader__btnIcon ${icon}`}></span>
                {text}
              </button>
            </li>
          ))}
          {SORT_LIST.map(({ code, icon, text }, index) => (
            <li key={code} className={index === 0 ? 'bar' : ''}>
              <button
                type="button"
                className={`viewerHeader__layerListBtn${code === screenSort ? ' selected' : ''}`}
                onClick={() => handleSetSort(code)}
              >
                <span className={`viewerHeader__btnIcon ${icon}`}></span>
                {text}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default ScreenModeButton;
